import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';

type ProgramBrandHeaderProps = {
  programName?: string | null;
  programLogoUrl?: string | null;
  defaultLogoUrl: string;
  logoAltText?: string | null;
  increaseFallbackLogoSize?: boolean;
  className?: string;
};

export function ProgramBrandHeader({
  programName,
  programLogoUrl,
  defaultLogoUrl,
  logoAltText,
  increaseFallbackLogoSize = true,
  className,
}: ProgramBrandHeaderProps) {
  const [programLogoFailed, setProgramLogoFailed] = useState(false);
  const [defaultLogoFailed, setDefaultLogoFailed] = useState(false);

  const normalizedProgramName = useMemo(() => {
    const value = programName?.trim();
    return value ? value : null;
  }, [programName]);

  const normalizedProgramLogoUrl = useMemo(() => {
    const value = programLogoUrl?.trim();
    return value ? value : null;
  }, [programLogoUrl]);

  const hasProgramLogo = Boolean(normalizedProgramLogoUrl && !programLogoFailed);
  const imageSrc: string = hasProgramLogo
    ? (normalizedProgramLogoUrl ?? defaultLogoUrl)
    : defaultLogoUrl;

  const logoSizeClass = hasProgramLogo
    ? 'h-[40.6px]'
    : increaseFallbackLogoSize
      ? 'h-[42.63px]'
      : 'h-[40.6px]';

  return (
    <div className={cn('flex flex-row items-center gap-2', className)}>
      {/* Logo or text fallback */}
      {defaultLogoFailed ? (
        <span className="text-[15px] font-bold text-primary tracking-tight flex-shrink-0">
          {logoAltText?.trim() || 'EPPREM'}
        </span>
      ) : (
        <img
          src={imageSrc}
          alt={hasProgramLogo ? `${normalizedProgramName || 'Program'} logo` : (logoAltText?.trim() || 'EPPREM')}
          className={cn(
            logoSizeClass,
            'w-auto object-contain max-w-[162.4px] transition-all duration-150 flex-shrink-0',
          )}
          onError={() => {
            if (hasProgramLogo) {
              setProgramLogoFailed(true);
              return;
            }
            setDefaultLogoFailed(true);
          }}
        />
      )}

      {/* Program name — beside logo, hidden on mobile */}
      {normalizedProgramName && (
        <>
          <span className="hidden sm:block w-px h-4 bg-border/60 flex-shrink-0" aria-hidden />
          <span
            className="hidden sm:block truncate text-[14px] font-medium text-foreground/85 max-w-[100px] md:max-w-[180px] lg:max-w-[240px]"
            title={normalizedProgramName}
          >
            {normalizedProgramName}
          </span>
        </>
      )}
    </div>
  );
}
